import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    fetchUserSettings,
    updateUserSetting,
    type UserSettingsMap
} from '../services/userSettings';
import { USER_SETTINGS_KEY } from './queryKeys';

export function useUserSettings(userId: string | undefined) {
    const query = useQuery({
        queryKey: USER_SETTINGS_KEY(userId ?? ''),
        queryFn: () => fetchUserSettings(userId as string),
        enabled: Boolean(userId),
        staleTime: 60_000
    });

    return {
        settings: query.data ?? {},
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error
    };
}

interface UpdateUserSettingArgs {
    category: string;
    key: string;
    value: boolean;
}

export function useUpdateUserSetting(userId: string) {
    const queryClient = useQueryClient();
    const queryKey = USER_SETTINGS_KEY(userId);

    return useMutation({
        mutationFn: ({ category, key, value }: UpdateUserSettingArgs) =>
            updateUserSetting(userId, category, key, value),
        onMutate: async ({ category, key, value }: UpdateUserSettingArgs) => {
            await queryClient.cancelQueries({ queryKey });
            const previousSettings = queryClient.getQueryData<UserSettingsMap>(queryKey);
            queryClient.setQueryData<UserSettingsMap>(queryKey, (prev) => ({
                ...prev,
                [category]: { ...prev?.[category], [key]: JSON.stringify(value) }
            }));
            return { previousSettings };
        },
        onError: (_err, _vars, context) => {
            queryClient.setQueryData(queryKey, context?.previousSettings);
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        }
    });
}
